import React, { useState, useRef, useMemo, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { RadioList } from './RadioList';
import { BetaTextInput } from '../BetaTextInput';
import { useForm } from 'react-hook-form';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { Loader } from '../common';

const CANCELLATION_REASON = 'cancellation_reason';
const CUSTOM_REASON = 'custom_reason';
const REASON = 'reason';
const modalTitle = TRANSLATIONS.BOOKING_TO_VOUCHER;

const ModalBookingCompletedToVoucher = ({
  isOpen,
  setIsOpen,
  token,
  order,
  motivation,
  customGoBackFunc,
  setResponseModal,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const firstStep = motivation ? 2 : 1;
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );

  const [step, setStep] = useState(firstStep);
  const [responseModalState, setResponseModalState] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });
  const actualSetResponseModal = setResponseModal || setResponseModalState;
  const [formData, setFormData] = useState({});
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [radioListError, setRadioListError] = useState(null);
  const [motivationError, setMotivationError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModalExit, setShowModalExit] = useState(false);

  const { register, handleSubmit, setValue } = useForm();

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      resetErrors();
      setShowModalExit(false);
      setRadioListError(null);
      setMotivationError(null);
    }
  }, [isOpen]);

  const goBackFun = () => {
    if (customGoBackFunc && step === 2) {
      customGoBackFunc();
    } else {
      setStep(step - 1);
    }
  };

  const formRef = useRef(null);
  const reasonslist = useMemo(
    () => ({
      cancellation_reason_1:
        TRANSLATIONS.ACTIVITY_WAS_CANCELLED_DUE_TO_THE_WEATHER,
      cancellation_reason_2: TRANSLATIONS.GUIDE_DID_NOT_SHOW_UP,
      other: TRANSLATIONS.OTHER,
    }),
    []
  );

  const handleNextStep = (data, event) => {
    event.preventDefault();

    if ((!data.reason || data.reason === '') && !showCustomInput) {
      setRadioListError(TRANSLATIONS.RADIO_LIST_ERROR);
      return;
    } else if ((!data.reason || data.reason === '') && showCustomInput) {
      setMotivationError(TRANSLATIONS.MOTIVATION_ERROR);
      return;
    }
    setFormData(data);
    setStep(step + 1);
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: 'completed_booking_to_voucher',
          reason: formData.reason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_BOOKING_TO_VOUCHER_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const form = (
    <form ref={formRef} onSubmit={handleSubmit(handleNextStep)}>
      <div className="pb-2xs">
        <RadioList
          inputName={CANCELLATION_REASON}
          options={reasonslist}
          defaultValue={null}
          onChange={(value) => {
            setValue(CANCELLATION_REASON, value);
            if (value === 'other') {
              setShowCustomInput(true);
              setValue(REASON, '');
            } else {
              setShowCustomInput(false);
              setValue(REASON, reasonslist[value]);
            }
          }}
          oneColumn={true}
          error={radioListError}
          setError={setRadioListError}
        />
      </div>
      {showCustomInput && (
        <>
          <BetaTextInput
            asTextArea
            className="modal-ord-li-review__review-form__field"
            label={TRANSLATIONS.TELL_US_YOUR_MOTIVATION}
            name={CUSTOM_REASON}
            setRef={register({ required: false, maxLength: { value: 500 } })}
            defaultValue={''}
            onChange={(event) => {
              // const value = event.target.value.replace(
              //   /[^a-zA-Z0-9\s.,!?'-]/g,
              //   ''
              // );
              const value = event.target.value;
              setValue(REASON, value);
              setValue(CUSTOM_REASON, value);
            }}
            resize={true}
            limit={500}
            error={motivationError}
            setError={setMotivationError}
            errorMessage={TRANSLATIONS.MOTIVATION_ERROR}
          />
        </>
      )}
      <input type="hidden" name="reason" ref={register} />
    </form>
  );

  const resetErrors = () => {
    setTimeout(() => {
      setRadioListError(null);
      setMotivationError(null);
    }, 500);
  };

  const firstStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <div>
            <Typography
              classNames="text-left"
              variant={TypographyVariants.p2}
              weight={TypographyWeights.bold}
            >
              {TRANSLATIONS.WE_ARE_SORRY_YOUR_ADVENTURE_DID_NOT_GO_AS_PLANNED}
            </Typography>
            <Typography classNames="text-left" variant={TypographyVariants.p3}>
              {
                TRANSLATIONS.HELP_US_BETTER_UNDERSTAND_WHAT_HAPPENED_PLEASE_SELECT_THE_REASON
              }
            </Typography>
          </div>
          {form}
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p4}
            state={TypographyStates['neutral-light']}
          >
            {
              TRANSLATIONS.WE_APPRECIATE_YOUR_FEEDBACK_EVERY_REPORT_HELPS_US_IMPROVE
            }
          </Typography>
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setShowModalExit(true)}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => {
              formRef.current.dispatchEvent(
                new Event('submit', { cancelable: true })
              );
            }}
          />
        </div>
      }
      handleAfterClose={resetErrors}
    />
  );

  const secondStepModal = (
    <ModalDrawer
      goBack={goBackFun}
      isOpen={isOpen && step === 2 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          {isLoading ? (
            <Loader loading={isLoading} />
          ) : (
            <>
              <Dialog
                align={DialogAlignments.row}
                type={DialogTypes.feature}
                content={TRANSLATIONS.HOW_DOES_THE_BOOKING_TO_VOUCHER_WORK}
                icon={
                  <BetaIcon
                    name={'info'}
                    color={IconColors.regular}
                    size={IconSizes[24]}
                  />
                }
              />
              <div className="d-flex flex-column gap-xs">
                <div
                  className="typography__p3 typography__neutral text-left"
                  dangerouslySetInnerHTML={{
                    __html: TRANSLATIONS.BOOKING_TO_VOUCHER_REQUEST_SUMMARY,
                  }}
                />
                <ModalOrderCard
                  order={order}
                  toVoucher={true}
                  motivation={formData.reason || motivation}
                />
              </div>
            </>
          )}
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            disabled={isLoading}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setShowModalExit(true)}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            disabled={isLoading}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONFIRM}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      }
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModalState.isOpen}
      setIsOpen={actualSetResponseModal}
      isSuccess={responseModalState.isSuccess}
      modalTitle={responseModalState.modalTitle}
      responseContent={responseModalState.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {responseModalDrawer}
      {firstStepModal}
      {secondStepModal}

      <ModalDrawerExitFlow
        isOpen={showModalExit}
        setIsOpen={setShowModalExit}
        closeAction={() => setIsOpen(false)}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      />
    </>
  );
};

ModalBookingCompletedToVoucher.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  motivation: PropTypes.string,
  customGoBackFunc: PropTypes.func,
  setResponseModal: PropTypes.func,
};

export { ModalBookingCompletedToVoucher };
